import { compact } from 'lodash';
import { DEFAULT_CROP_DFP_UUID } from 'constants/values';
import { formatYearToISO8601 } from 'util/formatters/dateFormatter';
import networkAdapter from '../adapters/network';
import { HYBRID_ONLY_COUNTRIES } from 'constants/countryCodes';
import { orchestrationLayerFetcher, rawOrchestrationLayerFetcher } from 'util/OL';
import { trackError } from 'util/error';
import { gql } from 'graphql-request';

export const fetchSeedProductsSearchResultsFromSprout = async ({
  brandUuid = undefined,
  cropDfpUuid = DEFAULT_CROP_DFP_UUID,
  advancedOnly = false,
  fragment,
  fieldCountryCode
}) => {
  const isHibrydOnlyCountry = HYBRID_ONLY_COUNTRIES.includes(fieldCountryCode);
  const type = isHibrydOnlyCountry ? 'HYBRID' : 'REC_ENABLED';

  const query = gql`
  query SeedProductsByLocation(
      $first: Int
      $countryCode: String
      $cropId: ID
      $updatedSince: DateTime
      $filterByFragment: String
      $seedProductDensityParameterType: SeedProductDensityParameterType
      $brandId: ID
    )
    {
      seedProductsByLocation(
        first: $first
        countryCode: $countryCode
        cropId: $cropId
        updatedSince: $updatedSince
        filterByFragment: $filterByFragment
        seedProductDensityParameterType: $seedProductDensityParameterType
        brandId: $brandId
      )
      {
        nodes {
          id
          name
          updatedAt
          cropId
          brandId
          brand {
            name
          }
          isGeneric
          relativeMaturity {
            unit
            value
          }
          legacyIds {
            id
            uuid
          }
        }
      }
    }`;
  const variables = {
    first: 100,
    countryCode: fieldCountryCode,
    cropId: cropDfpUuid,
    updatedSince: formatYearToISO8601(2010),
    filterByFragment: fragment,
    seedProductDensityParameterType: advancedOnly ? type : null,
    brandId: brandUuid
  };

  const { data, errors } = await rawOrchestrationLayerFetcher(query, variables);

  if (!data && errors) {
    const error = new TypeError('Could not fetch "SeedProductsByLocation" query from OL');
    trackError(error, errors);
    throw error;
  }

  return data;
};

export const fetchBrandsFromSprout = async ({ cropUuid, fieldCountryCode, results = [], after = null }) => {
  const query = gql`
    query Brands($cropId: ID, $countryCode: String, $after: String) {
      brands(cropId: $cropId, countryCode: $countryCode, after: $after) {
        nodes {
          id
          name
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }`;

  const variables = {
    cropId: cropUuid,
    countryCode: fieldCountryCode,
    after
  };

  try {
    const { brands } = await orchestrationLayerFetcher(query, variables);
    results.push(...brands.nodes);

    if (brands.pageInfo.hasNextPage) {
      return fetchBrandsFromSprout({ cropUuid, fieldCountryCode, results, after: brands.pageInfo.endCursor });
    }
    return results;
  } catch(error) {
    trackError(new TypeError('Could not fetch "Brands" query from OL'), error);
  }
};

export const fetchFertilizerSubstanceTypes = ({ userId }) => {
  const legacyOptions = {
    url: `/api/walter/v1/substances`,
    userId
  };

  return networkAdapter({
    legacyOptions
  });
};

export const fetchFertilityChemicalTypes = ({ userId }) => {
  const legacyOptions = {
    url: `/api/walter/v1/chemicalTypes`,
    userId
  };

  return networkAdapter({
    legacyOptions
  });
};

export const fetchChemicalsForChemicalTypeUuidAndSubstanceTypeUuid = ({
  chemicalTypeUuid,
  fragment,
  minPercent,
  substanceTypeUuid,
  userId,
  countryCode
}) => {
  const url = compact([
    `/api/walter/v2/chemicals?chemicalTypeId=${chemicalTypeUuid}&countryCode=${countryCode}`,
    substanceTypeUuid ? `primarySubstanceId=${substanceTypeUuid}` : null,
    fragment ? `fragment=${fragment}` : null,
    minPercent ? `substancePercentMin=${minPercent}` : null
  ]).join('&');
  const legacyOptions = {
    url,
    userId
  };

  return networkAdapter({
    legacyOptions
  });
};

export const fetchChemicalById = ({ id, userId }) => {
  const legacyOptions = {
    url: `/api/walter/v2/chemicals/${id}`,
    userId
  };

  return networkAdapter({
    legacyOptions
  });
};

export const fetchSeedByLegacyId = async (id) => {
  const query = gql`
  query SeedProductsByLegacyIds($ids: [ID!]!) {
      seedProductsByLegacyIds(ids: $ids) {
        updatedAt
        cropId
        name
        legacyIds {
          id
        }
        brand {
          name
        }
        isGeneric
        id
        relativeMaturity {
          unit
          value
        }
      }
    }`;
  const variables = { ids: [id.toString()] };

  try {
    return await orchestrationLayerFetcher(query, variables);
  } catch(error) {
    trackError(new TypeError('Could not fetch "SeedProductsByLegacyIds" query from OL.'), error);
  }
};

export const fetchSeedBySproutUuid = async (id) => {
  const query = gql`
  query SeedProduct($seedProductId: ID!) {
    seedProduct(id: $seedProductId) {
        updatedAt
        cropId
        name
        legacyIds {
          id
        }
        brand {
          name
        }
        isGeneric
        id
        relativeMaturity {
          unit
          value
        }
      }
    }`;
  const variables = { seedProductId: id };

  try {
    return await orchestrationLayerFetcher(query, variables);
  } catch(error) {
    trackError(new TypeError('Could not fetch "SeedProduct" query from OL.'), error);
  }
};

/**
 * @param {string} userId
 * @param {string} userUuid
 * @param {string} chemicalName
 * @param {string} chemicalTypeId
 * @param {number} matterState
 * @param {string} [matterState]
 * @param {Array<{ id: string, code: string, percentage: number}>} substances
*/
export const sendCreateCustomChemical = ({
  userId,
  userUuid,
  chemicalName,
  chemicalTypeId,
  matterState,
  specificGravity,
  substances
}) => {
  const legacyOptions = {
    extraHeaders: {
      // TODO Dont use same user for both
      'x-user-uuid': userUuid,
      'x-authenticated-user-uuid': userUuid
    },
    userId,
    url: '/api/walter/v2/chemicals/custom',
    method: 'POST',
    body: JSON.stringify({
      customChemicals: [{
        isCustom: true,
        matterState, // Default to 'liquid' for fertilizers until "I will be applying" matterState option are ready.
        specificGravity,
        name: chemicalName,
        partyType: 'USER',
        partyId: userUuid,
        chemicalTypeId,
        substances
      }]
    })
  };

  return networkAdapter({ legacyOptions });
};
